import { PlusOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useCreation, useLatest, useMemoizedFn, usePrevious } from 'ahooks';
import { Render } from 'components/Render';
import {
  Button,
  Form as AForm,
  FormInstance,
  FormProps,
  message,
  Modal,
  Radio,
  Spin,
  Tooltip,
  Space,
  Row,
} from 'antd';
import { useAccountSelect } from 'bizComponents/AccountSelect';
import { isAssignConversationRole } from 'bizComponents/RoleCascader';
import DecorateTitle from 'components/DecorateTitle';
import { RadioCard as RadioCardOrigin } from 'components/styled/radio';
import useModal from 'hooks/useModal';
import { isNil } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { scopesCannotChange } from 'services/api/department/permissions';
import {
  createRole,
  getRoleDetail,
  RoleWithDepartment,
  updateRole,
} from 'services/api/department/roleWithDepartment';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import { excludeRoleList, translateRole, useRoleMap } from 'views/Assistant/utils';
import PermissionsFields from '../../components/FormFields/PermissionsFields';
import useGetRoleDetail from '../../hooks/useGetRoleDetail';
import AssignConversationConfig from '../../RoleDetail/AssignConversationConfig';
import RoleDetailFormDrawer from '../../RoleDetail/RoleDetailFormDrawer';
import RolePermScopeType from '../../RoleDetail/RolePermScopeType';
import { isString } from 'lodash-es';

export interface AccountInDepartment
  extends StoreHomeV2.AccountInDepartment,
    Partial<
      Pick<
        RoleWithDepartment,
        'permissionWithScopes' | 'assignConversationConfig' | 'rolePermScopeType'
      >
    > {
  allScopes?: General.StoreScope[];
}

interface DepartmentDetail extends StoreHomeV2.DepartmentDetail {}

const FormItem = AForm.Item;
const Form = styled(AForm)``;

export interface DepartmentAccountDetailFormProps extends Omit<FormProps, 'initialValues'> {
  departmentDetail: DepartmentDetail;
  refreshDepartment: () => void;
  initialValues?: AccountInDepartment;
}

const defaultInitialValues: Partial<AccountInDepartment> = {
  // userId: 0,
  userName: '',
  userStatus: 'ENABLE',
  // roleId: 0,
  roleName: '',
  departmentId: 0,
  departmentName: '',
  assignConversation: false,
  permissionWithScopes: [],
};

const DepartmentAccountDetailForm = (props: DepartmentAccountDetailFormProps) => {
  const {
    className,
    departmentDetail,
    refreshDepartment,
    initialValues = defaultInitialValues,
    ...otherProps
  } = props;
  const { t } = useTranslate();
  const formRef = useRef<FormInstance<any>>(null);
  const [currentRole, setCurrentRole] = useState<number | undefined>(initialValues?.roleId);
  const currentRoleRef = useLatest(currentRole);
  const roleModal = useModal();
  const isInitialRoleRef = useRef(isNil(initialValues?.roleId));
  const { loading: roleLoading, refresh: refreshCurrentRole } = useGetRoleDetail(currentRole, {
    onSuccess: (data) => {
      const values = formRef?.current?.getFieldsValue(['userId', 'roleId']);

      const {
        rolePermScopeType = 'DIFF_BY_PERM',
        permissionWithScopes = [],
        assignConversationConfig,
      } = data || {};

      const firstCanChangeScope = permissionWithScopes?.find(
        ({ permissionName }) => !scopesCannotChange.includes(permissionName),
      );

      const allScopes =
        rolePermScopeType === 'ALL_PERM_ALIGNED'
          ? firstCanChangeScope?.platformStoreScopes || []
          : [];

      formRef?.current?.setFieldsValue({
        ...values,
        rolePermScopeType,
        allScopes,
        permissionWithScopes,
        assignConversationConfig,
      });
    },
  });
  const roles = useCreation(
    () => departmentDetail.roles.filter((r) => !excludeRoleList.some((er) => er === r.name)),
    [departmentDetail],
  );
  const preRoles = usePrevious(roles);
  const refsCurrent = {
    preRoles,
    setCurrentRole,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;
  useEffect(() => {
    const {
      current: { preRoles, setCurrentRole },
    } = refs;

    if (!preRoles) {
      return;
    }

    const newRole = roles.find(({ id }) => preRoles.every((role) => role.id !== id));

    if (newRole) {
      isInitialRoleRef.current = false;
      setCurrentRole(newRole.id);
    }
  }, [roles]);
  const changeRole = useMemoizedFn((e: any) => {
    if (isInitialRoleRef.current) {
      isInitialRoleRef.current = false;
      setCurrentRole(e.target.value);
      return;
    }
    const modal = Modal.confirm({
      title: t('department.确认要切换角色吗？'),
      content: t('department.切换角色后,该帐号将拥有新角色下的权限'),
      onCancel: () => {
        modal.destroy();
        if (isNil(currentRole) || !formRef.current) {
          return;
        }
        formRef.current?.setFieldsValue({
          roleId: currentRole,
        });
      },
      onOk: () => {
        setCurrentRole(e.target.value);
        modal.destroy();
      },
    });
  });

  const hasUserId = !isNil(initialValues?.userId);
  const accountFilter = useCreation(() => {
    if (hasUserId || isNil(departmentDetail?.accounts?.length)) {
      return undefined;
    }
    // 添加帐号时，过滤掉已在当前部门的帐号
    return (acc: StoreHomeV2.Account) => {
      return (
        !acc.isSuperAdmin &&
        !departmentDetail.accounts.some((target) => `${target.userId}` === `${acc.id}`)
      );
    };
  }, [hasUserId, departmentDetail]);

  useEffect(() => {
    if (currentRole !== undefined) {
      formRef.current?.setFieldsValue({
        roleId: currentRole,
      });
    }
  }, [currentRole]);

  const { list: accountList, select: accountSelect } = useAccountSelect({
    disabled: hasUserId,
    filter: accountFilter,
    showSearch: true,
    filterOption: (input, option) => {
      const label = option && isString(option.label) ? option.label : '';
      return label.toLowerCase().includes(input?.toLowerCase());
    },
  });

  const roleMap = useRoleMap(departmentDetail?.roles);

  const officialRole = useMemo(() => {
    return roleMap.get(currentRole)?.isDefault === 1;
  }, [roleMap, currentRole]);

  return (
    <Form layout="vertical" initialValues={initialValues} ref={formRef} {...otherProps}>
      <FormItem
        name="userId"
        label={
          hasUserId ? t('department.修改帐号') : t('department.选择x', { x: t('department.帐号') })
        }
        required={!hasUserId}
        rules={[
          {
            type: 'number',
            required: true,
            message: t('department.请输入x', { x: t('department.帐号') }),
          },
        ]}
      >
        {accountSelect}
      </FormItem>
      <FormItem noStyle dependencies={['roleId', 'userId']}>
        {({ getFieldsValue }) => {
          return (
            <Render>
              {() => {
                const { userId, roleId } = getFieldsValue();

                const _accountList = accountList;
                const _roles = roles;

                // eslint-disable-next-line react-hooks/rules-of-hooks
                const account = useMemo(
                  () => _accountList?.find((_) => _.id === userId),
                  [userId, _accountList],
                );

                // eslint-disable-next-line react-hooks/rules-of-hooks
                const role = useMemo(() => _roles.find((_) => _.id === roleId), [roleId, _roles]);

                // eslint-disable-next-line react-hooks/rules-of-hooks
                useEffect(() => {
                  if (account?.type === 2 && (!role || isAssignConversationRole(role))) {
                    setCurrentRole(_roles.find((role) => !isAssignConversationRole(role))?.id);
                  }
                }, [account, role, _roles]);

                return (
                  <FormItem
                    name="roleId"
                    label={t('department.选择角色后即包含该角色下的权限')}
                    required
                    rules={[
                      {
                        type: 'number',
                        required: true,
                        message: t('department.请选择x', { x: t('department.角色') }),
                      },
                    ]}
                  >
                    <Radio.Group value={currentRole} onChange={changeRole}>
                      {roles?.map((r) => {
                        return account?.type === 2 && isAssignConversationRole(r) ? (
                          <Tooltip
                            title={t('account.接待限额帐号不支持选择分配模式为智能分配的角色')}
                          >
                            <RadioCard disabled key={r.id} value={r.id}>
                              {translateRole(r.name, t)}
                            </RadioCard>
                          </Tooltip>
                        ) : (
                          <RadioCard key={r.id} value={r.id}>
                            {translateRole(r.name, t)}
                          </RadioCard>
                        );
                      })}
                    </Radio.Group>
                    <Row>
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={() => roleModal.show({ initialValues: { copyRoleId: roleId } })}
                      >
                        {t('department.新建角色')}
                      </Button>
                    </Row>
                  </FormItem>
                );
              }}
            </Render>
          );
        }}
      </FormItem>
      <RoleDetailFormDrawer
        department={departmentDetail}
        title={t('department.角色')}
        destroyOnClose
        formProps={{
          initialValues: (roleModal.meta as any)?.initialValues,
          partialMode: (roleModal.meta as any)?.partialMode,
          onFinish: async (values) => {
            const {
              rolePermScopeType,
              allScopes = [],
              permissionWithScopes,
              assignConversationConfig,
              ...others
            } = values;

            const submitValue = {
              ...others,
              rolePermScopeType,
              permissionWithScopes:
                rolePermScopeType === 'DIFF_BY_PERM'
                  ? permissionWithScopes
                  : permissionWithScopes.map((permission) => {
                      const { permissionName } = permission;

                      if (scopesCannotChange.includes(permissionName)) {
                        return permission;
                      }

                      return {
                        ...permission,
                        platformStoreScopes: allScopes,
                      };
                    }),
              assignConversationConfig: {
                ...assignConversationConfig,
                platformStoreScopes:
                  rolePermScopeType === 'DIFF_BY_PERM'
                    ? assignConversationConfig.platformStoreScopes
                    : allScopes,
              },
            };

            const isCreate = isNil(submitValue.id);
            try {
              if (isCreate) {
                await createRole({ ...submitValue, departmentId: departmentDetail.id });
                message.success(t('department.已创建角色'));
              } else {
                await updateRole(submitValue);
                message.success(t('department.已更新角色'));
              }
              refreshDepartment();
              refreshCurrentRole();
              roleModal.hide();
            } catch (e) {
              errorToasterWithServerMsg(e);
            }
          },
        }}
        visible={roleModal.visible}
        onClose={() => roleModal.hide()}
      />
      <Spin spinning={roleLoading}>
        <FieldTitle>
          <Space>
            <span>{t('department.权限配置')}</span>
            {currentRoleRef.current &&
              (officialRole ? (
                <SubTitle>{t('department.officialRoleNotAllowed')}</SubTitle>
              ) : (
                <SubTitle
                  hover
                  onClick={async () => {
                    if (currentRoleRef.current) {
                      try {
                        const role = await getRoleDetail(currentRoleRef.current).then(
                          (r) => r?.data?.data,
                        );
                        roleModal.show({
                          initialValues: Object.assign({}, role, {
                            copyRoleId: currentRoleRef.current,
                          }),
                          partialMode: true,
                        });
                      } catch (e) {
                        errorToasterWithServerMsg(e, t('department.拉取角色数据失败，请稍后再试'));
                      }
                    }
                  }}
                >{`${t('department.修改权限')} >`}</SubTitle>
              ))}
          </Space>
        </FieldTitle>
        <RolePermScopeType
          departmentId={departmentDetail.id}
          departmentScopes={departmentDetail.platformStoreScopes}
          disabled
        />
        <AssignConversationConfig departmentId={departmentDetail.id} disabled roleId={undefined} />
        <PermissionsFields
          departmentId={departmentDetail.id}
          departmentScopes={departmentDetail.platformStoreScopes}
          disabled
          label={t('department.选择角色权限')}
          name="permissionWithScopes"
          required
          shouldUpdate
          initialValue={initialValues?.permissionWithScopes ?? []}
        />
      </Spin>
    </Form>
  );
};

const RadioCard = styled(RadioCardOrigin)`
  margin: 8px;
`;

const FieldTitle = styled(DecorateTitle)`
  margin-left: 16px;
`;

const SubTitle = styled.span<{ hover?: boolean }>`
  font-size: 14px;
  color: var(--fett-text-secondary);
  font-weight: normal;
  ${(props) => props.hover && 'cursor: pointer;&:hover {color: rgb(51, 102, 255)} '}
`;

export default DepartmentAccountDetailForm;
